import { CACHE_TIME } from "@/lib/cacheConfig";
import { useQueryInvalidation } from "@/lib/queryInvalidation";
import { queryKeys } from "@/lib/queryKeys";
import { useQuery, useQueries, useMutation } from "@tanstack/react-query";
import {
  createMaterialType,
  createSpec,
  getAllMaterialTypes,
  getSpecsByMaterialTypeId,
  updateMaterialType,
  updateSpec,
  type CreateMaterialType,
  type CreateSpecType,
  type getMaterialTypeParams,
  type MaterialTypeSpec,
} from "@/api/materialTypeService";

export const useGetAllMaterialTypes = (params: getMaterialTypeParams) => {
  const query = useQuery({
    queryKey: [queryKeys.materialTypes.list(), params],
    queryFn: () => getAllMaterialTypes(params),
    staleTime: CACHE_TIME.MASTER_DATA,
  });

  return query;
};

export const useGetSpecsByMaterialTypeId = (
  materialTypeId: number,
  params: getMaterialTypeParams
) => {
  const query = useQuery({
    queryKey: [queryKeys.materialTypes.specs(materialTypeId), params],
    queryFn: () => getSpecsByMaterialTypeId(materialTypeId, params),
    staleTime: CACHE_TIME.MASTER_DATA,
    enabled: !!materialTypeId,
  });

  return query;
};

export const useGetSpecsForOpenRows = (
  openMaterialTypeIds: number[],
  params: getMaterialTypeParams
) => {
  const queries = useQueries({
    queries: openMaterialTypeIds.map((materialTypeId) => ({
      queryKey: [queryKeys.materialTypes.specs(materialTypeId), params],
      queryFn: () => getSpecsByMaterialTypeId(materialTypeId, params),
      staleTime: CACHE_TIME.MASTER_DATA,
      enabled: !!materialTypeId,
    })),
  });

  const specsData: { [materialTypeId: number]: MaterialTypeSpec[] } = {};
  const isLoading: { [materialTypeId: number]: boolean } = {};

  openMaterialTypeIds.forEach((materialTypeId, index) => {
    const queryResult = queries[index];
    if (queryResult.data) {
      specsData[materialTypeId] = queryResult.data;
      isLoading[materialTypeId] = queryResult.isLoading;
    }
  });

  const isError = queries.some((q: { isError: boolean }) => q.isError);

  return { data: specsData, isLoading, isError };
};

export const useCreateMaterialType = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.materialTypes.create(),
    mutationFn: (params: CreateMaterialType) => createMaterialType(params),
    onSuccess: () => {
      queryInvalidation.invalidateMaterialTypesList();
      queryInvalidation.invalidateBasePrices();
    },
  });
};

export const useUpdateMaterialType = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.materialTypes.update(),
    mutationFn: (params: { id: number; data: CreateMaterialType }) =>
      updateMaterialType(params.id, params.data),
    onSuccess: () => {
      queryInvalidation.invalidateMaterialTypesList();
      queryInvalidation.invalidateBasePrices();
    },
  });
};

export const useCreateSpec = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.materialTypes.createSpec(),
    mutationFn: (params: CreateSpecType) => createSpec(params),
    onSuccess: (_, variables) => {
      queryInvalidation.invalidateMaterialTypesList();
      if (variables.material_type_id) {
        queryInvalidation.invalidateMaterialTypeSpecs(variables.material_type_id);
      }
      queryInvalidation.invalidateBasePrices();
    },
  });
};

export const useUpdateSpec = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.materialTypes.updateSpec(),
    mutationFn: ({ id, params }: { id: number; params: CreateSpecType }) => updateSpec(id, params),
    onSuccess: (_, variables) => {
      queryInvalidation.invalidateMaterialTypesList();
      if (variables.params.material_type_id) {
        queryInvalidation.invalidateMaterialTypeSpecs(variables.params.material_type_id);
      }
      queryInvalidation.invalidateBasePrices();
    },
  });
};